// src/hooks/useExamSessions.js
import { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';

/**
 * useExamSessions — mirrors your useIntakes API
 *
 * Exposes:
 *  - examSessions, loading, error
 *  - addExamSession(formData)         -> { success, data?, error? }
 *  - updateExamSession(id, formData)  -> { success, data?, error? }
 *  - deleteExamSession(id)            -> { success, error? }
 *  - refetch()                        -> void (optional, handy after external ops)
 *
 * Table: rism.exam_sessions
 * Columns: id, name, start_date, end_date, notes, created_at, updated_at
 */

const TABLE = 'exam_sessions'; // or 'exam_sessions' if your schema is defaulted

export function useExamSessions() {
  const [examSessions, setExamSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Initial fetch
  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError('');
      try {
        const { data, error: err } = await supabase
          .from(TABLE)
          .select('id, name, start_date, end_date, notes, created_at, updated_at')
          .order('start_date', { ascending: false })
          .order('name', { ascending: true });

        if (err) throw err;
        if (!cancelled) setExamSessions(data || []);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load exam sessions');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Public refetch (optional)
  const refetch = async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await supabase
        .from(TABLE)
        .select('id, name, start_date, end_date, notes, created_at, updated_at')
        .order('start_date', { ascending: false })
        .order('name', { ascending: true });

      if (err) throw err;
      setExamSessions(data || []);
    } catch (e) {
      setError(e?.message || 'Failed to load exam sessions');
    } finally {
      setLoading(false);
    }
  };

  // Create
  const addExamSession = async (formData) => {
    try {
      // Expecting: { name, start_date, end_date, notes? }
      const payload = {
        name: formData?.name ?? '',
        start_date: formData?.start_date ?? null,
        end_date: formData?.end_date ?? null,
        notes: formData?.notes ?? null,
      };

      const { data, error: err } = await supabase
        .from(TABLE)
        .insert(payload)
        .select()
        .single();

      if (err) throw err;

      // optimistic append (sorted fetch is fine too)
      setExamSessions((prev) => [data, ...prev]);
      return { success: true, data };
    } catch (e) {
      // Friendly duplicate-name message if unique(name) violated
      const msg =
        e?.code === '23505' || /duplicate key value/i.test(String(e?.message))
          ? 'A session with this name already exists.'
          : e?.message || 'Failed to create exam session.';
      return { success: false, error: msg };
    }
  };

  // Update
  const updateExamSession = async (id, formData) => {
    try {
      const payload = {
        name: formData?.name ?? '',
        start_date: formData?.start_date ?? null,
        end_date: formData?.end_date ?? null,
        notes: formData?.notes ?? null,
      };

      const { data, error: err } = await supabase
        .from(TABLE)
        .update(payload)
        .eq('id', id)
        .select()
        .single();

      if (err) throw err;

      // optimistic replace in local state
      setExamSessions((prev) => prev.map((s) => (s.id === id ? data : s)));
      return { success: true, data };
    } catch (e) {
      const msg =
        e?.code === '23505' || /duplicate key value/i.test(String(e?.message))
          ? 'A session with this name already exists.'
          : e?.message || 'Failed to update exam session.';
      return { success: false, error: msg };
    }
  };

  // Delete
  const deleteExamSession = async (id) => {
    try {
      const { error: err } = await supabase.from(TABLE).delete().eq('id', id);
      if (err) throw err;

      setExamSessions((prev) => prev.filter((s) => s.id !== id));
      return { success: true };
    } catch (e) {
      return { success: false, error: e?.message || 'Failed to delete exam session.' };
    }
  };

  return {
    examSessions,
    loading,
    error,
    addExamSession,
    updateExamSession,
    deleteExamSession,
    refetch,
  };
}

export default useExamSessions;
